import {
    BASE_VALUE,
    BulkSelectionContextValue,
} from '@/components/bulkQueue/tables/BulkSelectionContext';
import {
    Entity,
    getOrCreateEntityMap,
    useBulkSelectionStore,
    useBulkState,
    useSelectedEntityIdsKey,
} from '@/components/bulkQueue/tables/bulkContextUtils';
import { BULK_CLINICIAN_ASSIGNMENT_TABLE_QUERY } from '@/components/bulkQueue/tables/clinician/BulkClinicianAssignmentTable';
import {
    BulkClinicianRecommendationsQuery,
    BulkClinicianRecommendationsQueryVariables,
    BulkUpdateClinicianAssignmentMutation,
    BulkUpdateClinicianAssignmentMutationVariables,
    BulkUpdateClinicianVisitAssignmentMutation,
    BulkUpdateClinicianVisitAssignmentMutationVariables,
} from '@/components/bulkQueue/tables/clinician/BulkClinicianContext.gql.types';
import {
    UseAssignmentClinicianRecsResult,
    useAssignmentClinicianRecs,
} from '@/components/decisionSupport/hooks/useAssignmentClinicianRecs';
import {
    VISIT_RECOMMENDED_CLINICIANS_BASE_FRAGMENT,
    VISIT_REQUEST_RECOMMENDED_CLINICIANS_BASE_FRAGMENT,
} from '@/components/decisionSupport/queries/clinicianRecommendations';
import {
    ClinicianRecommendationFragment,
    VisitRecommendedCliniciansBaseFragment,
    VisitRequestRecommendedCliniciansBaseFragment,
} from '@/components/decisionSupport/queries/clinicianRecommendations.gql.types';
import { clinicianRecommendationToSelection } from '@/components/decisionSupport/utils/clinicianRecommendations.utils';
import {
    DecisionSupportSelection,
    canSelectVisit,
    pickTopRanked,
} from '@/components/decisionSupport/utils/decisionSupport.utils';
import { VisitOverrideState } from '@/components/decisionSupport/utils/visitAssignmentInput.utils';
import { ALL_VISIT_COUNTS } from '@/pages/AllVisitsPage/AllVisits.gql';
import { onError } from '@/toast';
import { FeatureFlag } from '@/types/featureFlag';
import {
    ProviderStore,
    ProviderWritableStore,
    createDefaultProviderStore,
    createDefaultWritableProviderStore,
    createStore,
    useStoreValue,
} from '@/utils/store';
import { useFeatureFlagEnabled } from '@/utils/useFeatureFlagEnabled';
import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import {
    UpdateClinicianAppointmentClinicianAssignmentInput,
    UpdateVisitRequestAppointmentClinicianAssignmentInput,
} from 'gql-types';
import {
    ReactNode,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
} from 'react';
import { useSyncExternalStore } from 'use-sync-external-store/shim';

export const BULK_CLINICIAN_RECOMMENDATIONS = gql`
    query BulkClinicianRecommendations(
        $visitIds: [UUID!]!
        $excludeVisits: Boolean!
        $visitRequestIds: [UUID!]!
        $excludeVisitRequests: Boolean!
    ) {
        decisionSupport {
            visitRequests(filters: { id: { inList: $visitRequestIds } })
                @skip(if: $excludeVisitRequests) {
                __typename
                clinicianRecommendations {
                    ...VisitRequestRecommendedCliniciansBase
                }
            }
            visits(filters: { id: { inList: $visitIds } }) @skip(if: $excludeVisits) {
                clinicianRecommendations {
                    ...VisitRecommendedCliniciansBase
                }
            }
        }
    }
    ${VISIT_REQUEST_RECOMMENDED_CLINICIANS_BASE_FRAGMENT}
    ${VISIT_RECOMMENDED_CLINICIANS_BASE_FRAGMENT}
`;

const BULK_UPDATE_CLINICIAN_VISIT_REQUEST_ASSIGNMENT = gql`
    mutation BulkUpdateClinicianAssignment(
        $input: BulkUpdateVisitRequestAppointmentClinicianAssignmentInput!
    ) {
        bulkUpdateVisitRequestAppointmentClinicianAssignment(input: $input) {
            success
            successCount
            errorCount
        }
    }
`;

const BULK_UPDATE_CLINICIAN_VISIT_ASSIGNMENT = gql`
    mutation BulkUpdateClinicianVisitAssignment(
        $input: BulkUpdateClinicianAppointmentClinicianAssignmentInput!
    ) {
        bulkUpdateClinicianAppointmentClinicianAssignment(input: $input) {
            success
            successCount
            errorCount
        }
    }
`;

type AssignmentClinicianResult =
    UseAssignmentClinicianRecsResult['resultsByVisitId'] extends ReadonlyMap<string, infer V>
        ? V
        : never;
export type AssignmentClinicianRecommendation =
    AssignmentClinicianResult['clinicianRecommendation'];
type AssignmentDefinitions = UseAssignmentClinicianRecsResult['definitions'];

// Whether the user has explicitly picked a clinician for at least one of a visit's appointments
const hasUserPick = (
    selection: Map<string, DecisionSupportSelection | null> | null | undefined,
): boolean => (selection ? Array.from(selection.values()).some((s) => s != null) : false);

type BulkClinicianContextValue = BulkSelectionContextValue<{
    recommendationsStore: ProviderStore<Map<string, ClinicianRecommendationFragment[]>>;
    selectedRecommendationStore: ProviderWritableStore<
        Map<string, DecisionSupportSelection | null>
    >;
    // New DS visitAssignment flow (DS_VISIT_ASSIGNMENT_REWRITE). When `hasRewrite`
    // is true the cells read clinician recommendations from these instead of the
    // legacy `recommendationsStore`; selection/submit still flow through the
    // shared `selectedRecommendationStore`.
    hasRewrite: boolean;
    assignmentDefinitions: AssignmentDefinitions;
    assignmentResultsByVisitId: UseAssignmentClinicianRecsResult['resultsByVisitId'];
    assignmentSelectableIds: string[];
}>;

const DEFAULT_VALUE: BulkClinicianContextValue = {
    ...BASE_VALUE,
    recommendationsStore: createDefaultProviderStore(new Map()),
    selectedRecommendationStore: createDefaultWritableProviderStore(new Map()),
    hasRewrite: false,
    assignmentDefinitions: null,
    assignmentResultsByVisitId: new Map(),
    assignmentSelectableIds: [],
};

type FetchRecommendedCliniciansArgs = {
    visitIds: string[];
    visitRequestIds: string[];
};

const useFetchRecommendedClinicians = (options: FetchRecommendedCliniciansArgs) => {
    const { visitRequestIds, visitIds } = options;
    const excludeVisits = visitIds.length === 0;
    const excludeVisitRequests = visitRequestIds.length === 0;

    const { data, previousData, loading } = useQuery<
        BulkClinicianRecommendationsQuery,
        BulkClinicianRecommendationsQueryVariables
    >(BULK_CLINICIAN_RECOMMENDATIONS, {
        variables: {
            visitIds,
            excludeVisits,
            visitRequestIds,
            excludeVisitRequests,
        },
        skip: excludeVisits && excludeVisitRequests,
        fetchPolicy: 'network-only',
    });

    const currentData = data ?? previousData;

    return useMemo(() => {
        const recommendations = new Map<string, Map<string, ClinicianRecommendationFragment[]>>();
        const selectedClinicians = new Map<string, Map<string, DecisionSupportSelection | null>>();

        const addRecommendation = (recommendation: RecommendedClinicians) => {
            const {
                clinicianAppointment: { id, entity },
                clinicianRecommendations: recs,
            } = recommendation;
            if (entity) {
                const { id: entityId } = entity;
                getOrCreateEntityMap(recommendations, entityId).set(id, recs);
                const topRec = recs.at(0);
                getOrCreateEntityMap(selectedClinicians, entityId).set(
                    id,
                    topRec ? clinicianRecommendationToSelection(topRec) : null,
                );
            }
        };

        const { visitRequests, visits } = currentData?.decisionSupport ?? {};
        visitRequests?.clinicianRecommendations?.forEach(addRecommendation);
        visits?.clinicianRecommendations?.forEach(addRecommendation);

        return { recommendations, selectedClinicians, loading };
    }, [currentData, loading]);
};

type RecommendedClinicians =
    | VisitRecommendedCliniciansBaseFragment
    | VisitRequestRecommendedCliniciansBaseFragment;

const BulkClinicianContext = createContext<BulkClinicianContextValue>(DEFAULT_VALUE);

type BulkClinicianContextProviderProps = {
    children: ReactNode;
    entities: Entity[];
};

export const BulkClinicianContextProvider = (props: BulkClinicianContextProviderProps) => {
    const { children, entities } = props;
    const [selectionStore, setValue, selectedCount, resetSelection, pruneToEntityIds] =
        useBulkSelectionStore();
    const recommendationsStoreRef = useRef(
        createStore<Map<string, ClinicianRecommendationFragment[]>>(),
    );
    const recommendationsStore = recommendationsStoreRef.current;
    const selectedClinicianStoreRef = useRef(
        createStore<Map<string, DecisionSupportSelection | null>>(),
    );
    const selectedClinicianStore = selectedClinicianStoreRef.current;

    // Prune selections when the displayed entities change (e.g. filter change).
    useEffect(() => {
        const entityIds = new Set(entities.map((e) => e.id));
        pruneToEntityIds(entityIds);
    }, [entities, pruneToEntityIds]);

    const visitIds = useMemo(
        () => entities.filter((entity) => entity.__typename === 'Visit').map((entity) => entity.id),
        [entities],
    );

    const visitRequestIds = useMemo(
        () =>
            entities
                .filter((entity) => entity.__typename === 'VisitRequest')
                .map((entity) => entity.id),
        [entities],
    );

    const hasRewrite = useFeatureFlagEnabled(FeatureFlag.DS_VISIT_ASSIGNMENT_REWRITE);

    // New DS visitAssignment flow (visits only). Clinician recommendations choose
    // the clinician (no clinician override sent) with the date held implicitly.
    const visitOverrideStates = useMemo<VisitOverrideState[]>(
        () =>
            hasRewrite
                ? entities
                      .filter((entity) => entity.__typename === 'Visit')
                      .map((entity) => ({
                          visitId: entity.id,
                          appointments: [],
                          dateOverride: undefined,
                      }))
                : [],
        [hasRewrite, entities],
    );
    const {
        definitions: assignmentDefinitions,
        resultsByVisitId: assignmentResultsByVisitId,
        loading: assignmentLoading,
    } = useAssignmentClinicianRecs(visitOverrideStates);

    // Legacy fetch handles VisitRequests (and all entities when the flag is off).
    // When the flag is on, exclude visits so they only flow through the new query.
    const { recommendations, selectedClinicians, loading } = useFetchRecommendedClinicians({
        visitIds: hasRewrite ? [] : visitIds,
        visitRequestIds,
    });

    // Populate the legacy stores from the legacy fetch (flag off, or VR entities).
    useEffect(() => {
        if (hasRewrite) {
            return;
        }
        recommendationsStore.setAll(recommendations);
        selectedClinicianStore.setAll(selectedClinicians);
    }, [
        hasRewrite,
        recommendations,
        recommendationsStore,
        selectedClinicianStore,
        selectedClinicians,
    ]);

    // The store holds only the user's picks.
    // the recommended default is derived at read/submit (see getDefaultClinician)
    useEffect(() => {
        if (!hasRewrite) {
            return;
        }
        selectedClinicianStore.setAll(new Map());
    }, [hasRewrite, assignmentResultsByVisitId, selectedClinicianStore]);

    const getDefaultClinician = useCallback(
        (entityId: string): DecisionSupportSelection | null => {
            const recs =
                assignmentResultsByVisitId.get(entityId)?.clinicianRecommendation.recommendations;
            const top = pickTopRanked(recs);
            return top ? { id: top.clinician.id, label: top.clinician.fullName } : null;
        },
        [assignmentResultsByVisitId],
    );

    // Recomputes the selectable set (Select All + header count) when the user picks;
    // the selection store mutates outside React (see useSelectedEntityIdsKey).
    const pickedVisitIdsKey = useSelectedEntityIdsKey(selectedClinicianStore, hasUserPick);

    // A visit is selectable when it has a recommended default OR an explicit pick
    const assignmentSelectableIds = useMemo<string[]>(() => {
        if (!hasRewrite) {
            return [];
        }
        const pickedVisitIds = new Set(pickedVisitIdsKey ? pickedVisitIdsKey.split(',') : []);
        return entities
            .filter((entity) => entity.__typename === 'Visit')
            .filter((entity) =>
                canSelectVisit(
                    (assignmentResultsByVisitId.get(entity.id)?.clinicianRecommendation
                        .recommendations.length ?? 0) > 0,
                    pickedVisitIds.has(entity.id),
                ),
            )
            .map((entity) => entity.id);
    }, [hasRewrite, entities, assignmentResultsByVisitId, pickedVisitIdsKey]);

    const [mutateVisitRequests, { loading: submitting }] = useMutation<
        BulkUpdateClinicianAssignmentMutation,
        BulkUpdateClinicianAssignmentMutationVariables
    >(BULK_UPDATE_CLINICIAN_VISIT_REQUEST_ASSIGNMENT, { onError });

    const [mutateVisits, { loading: submittingVisit }] = useMutation<
        BulkUpdateClinicianVisitAssignmentMutation,
        BulkUpdateClinicianVisitAssignmentMutationVariables
    >(BULK_UPDATE_CLINICIAN_VISIT_ASSIGNMENT, { onError });

    const submit = useCallback(async () => {
        const selected = selectionStore.getAll();
        const visitAppointments: UpdateClinicianAppointmentClinicianAssignmentInput[] = [];
        const visitRequestAssignments: UpdateVisitRequestAppointmentClinicianAssignmentInput[] = [];

        selected.forEach((isSelected, entityId) => {
            if (!isSelected) {
                return;
            }
            const entity = entities.find((e) => e.id === entityId);
            const stored = selectedClinicianStore.getSnapshot(entityId);

            if (entity?.__typename === 'VisitRequest') {
                // Legacy path: the store is seeded for VisitRequests.
                stored?.forEach((selection, appointmentId) => {
                    if (!selection?.id) {
                        return;
                    }
                    visitRequestAssignments.push({
                        clinicianAppointmentId: appointmentId,
                        clinicianId: selection.id,
                        visitRequestId: entityId,
                    });
                });
                return;
            }

            // New flow (Visit): derive pick ?? top clinician per appointment.
            (entity?.appointments ?? []).forEach((appointment) => {
                const selection = stored?.get(appointment.id) ?? getDefaultClinician(entityId);
                if (!selection?.id) {
                    return;
                }
                visitAppointments.push({
                    clinicianAppointmentId: appointment.id,
                    clinicianId: selection.id,
                });
            });
        });

        const visitPromise =
            visitAppointments.length > 0
                ? mutateVisits({
                      variables: { input: { clinicianAppointments: visitAppointments } },
                      refetchQueries: [ALL_VISIT_COUNTS, BULK_CLINICIAN_ASSIGNMENT_TABLE_QUERY],
                      awaitRefetchQueries: true,
                  })
                : undefined;
        const visitRequestPromise =
            visitRequestAssignments.length > 0
                ? mutateVisitRequests({
                      variables: { input: { visitRequests: visitRequestAssignments } },
                      refetchQueries: [ALL_VISIT_COUNTS, BULK_CLINICIAN_ASSIGNMENT_TABLE_QUERY],
                      awaitRefetchQueries: true,
                  })
                : undefined;

        await Promise.all([visitPromise, visitRequestPromise].filter(Boolean));
        resetSelection();
    }, [
        mutateVisits,
        mutateVisitRequests,
        selectionStore,
        selectedClinicianStore,
        getDefaultClinician,
        entities,
        resetSelection,
    ]);

    const state = useBulkState(loading || assignmentLoading, submitting || submittingVisit);

    const value: BulkClinicianContextValue = {
        isEntitySelectedStore: {
            ...selectionStore,
            setValue,
        },
        recommendationsStore,
        selectedRecommendationStore: selectedClinicianStore,
        submit,
        state,
        selectedCount,
        entityCount: entities.length,
        hasRewrite,
        assignmentDefinitions,
        assignmentResultsByVisitId,
        assignmentSelectableIds,
    };

    return <BulkClinicianContext.Provider value={value}>{children}</BulkClinicianContext.Provider>;
};

const getSelectableEntityIds = (
    recommendationsStore: ProviderStore<Map<string, ClinicianRecommendationFragment[]>>,
): string[] => {
    const ids: string[] = [];
    recommendationsStore.getAll().forEach((appointmentMap, entityId) => {
        for (const recs of appointmentMap.values()) {
            if (recs.length > 0) {
                ids.push(entityId);
                break;
            }
        }
    });
    return ids;
};

export const useBulkClinicianContext = () => {
    const {
        submit,
        state,
        selectedCount,
        entityCount,
        recommendationsStore,
        isEntitySelectedStore,
        hasRewrite,
        assignmentSelectableIds,
    } = useContext(BulkClinicianContext);
    const summary = useClinicianSummary();

    const legacySelectableCount = useSyncExternalStore(
        (cb: () => void) => recommendationsStore.subscribeToAll(cb),
        () => getSelectableEntityIds(recommendationsStore).length,
    );
    const selectableCount = hasRewrite ? assignmentSelectableIds.length : legacySelectableCount;

    const selectAll = useCallback(() => {
        const selectableIds = hasRewrite
            ? assignmentSelectableIds
            : getSelectableEntityIds(recommendationsStore);
        const currentlySelected = isEntitySelectedStore.getAll();
        const allSelected =
            selectableIds.length > 0 &&
            selectableIds.every((id) => currentlySelected.get(id) === true);
        selectableIds.forEach((id) => {
            isEntitySelectedStore.setValue?.(id, !allSelected);
        });
    }, [hasRewrite, assignmentSelectableIds, recommendationsStore, isEntitySelectedStore]);

    return useMemo(
        () => ({
            submit,
            state,
            selectedCount,
            entityCount,
            summary,
            selectableCount,
            selectAll,
        }),
        [selectedCount, summary, entityCount, state, submit, selectableCount, selectAll],
    );
};

export const useClinicianSummary = () => {
    const { selectedRecommendationStore, isEntitySelectedStore } = useContext(BulkClinicianContext);

    return useSyncExternalStore(
        (cb: () => void) => {
            const unsub1 = selectedRecommendationStore.subscribeToAll(cb);
            const unsub2 = isEntitySelectedStore.subscribeToAll(cb);
            return () => {
                unsub1();
                unsub2();
            };
        },
        () => {
            const clinicianIds = new Set<string>();
            const clinicians: DecisionSupportSelection[] = [];
            selectedRecommendationStore.getAll().forEach((appointmentMap) => {
                appointmentMap.forEach((selection) => {
                    if (selection && !clinicianIds.has(selection.id)) {
                        clinicianIds.add(selection.id);
                        clinicians.push(selection);
                    }
                });
            });
            const list = clinicians.map((c) => c.label).join(', ');
            const label = `clinician${clinicians.length > 1 ? 's' : ''}`;
            const numSelected = Array.from(isEntitySelectedStore.getAll().values()).filter(
                Boolean,
            ).length;
            return `Assign ${label} to ${numSelected} visits: ${list}`;
        },
    );
};

export const useIsClinicianSelected = (
    entityId: string,
): [boolean, (isSelected: boolean) => void] => {
    const { isEntitySelectedStore } = useContext(BulkClinicianContext);
    const isSelected = useStoreValue(BulkClinicianContext, 'isEntitySelectedStore', entityId);

    const setIsSelected = useCallback(
        (selected: boolean) => {
            isEntitySelectedStore.setValue?.(entityId, selected);
        },
        [isEntitySelectedStore, entityId],
    );

    return [isSelected, setIsSelected];
};

export const useCanSelectClinicianVisit = (entityId: string): boolean => {
    const { hasRewrite, assignmentResultsByVisitId } = useContext(BulkClinicianContext);
    const recommendationsMap = useStoreValue(
        BulkClinicianContext,
        'recommendationsStore',
        entityId,
    );
    // Subscribe to the user's picks so the checkbox appears once a clinician is
    // chosen for a visit that has no recommended default (e.g. incompatible-only).
    const selectedMap = useStoreValue(
        BulkClinicianContext,
        'selectedRecommendationStore',
        entityId,
    );

    if (hasRewrite) {
        const hasRecommendation =
            (assignmentResultsByVisitId.get(entityId)?.clinicianRecommendation.recommendations
                .length ?? 0) > 0;
        return canSelectVisit(hasRecommendation, hasUserPick(selectedMap));
    }
    if (!recommendationsMap) {
        return false;
    }
    let hasRecs = false;
    recommendationsMap.forEach((recs) => {
        if (recs.length > 0) {
            hasRecs = true;
        }
    });
    return hasRecs;
};

// Whether the new DS visitAssignment flow is active for this table.
export const useClinicianRewriteEnabled = (): boolean =>
    useContext(BulkClinicianContext).hasRewrite;

// Per-visit clinician recommendation + shared definitions for the new flow.
export const useAssignmentClinicianRec = (
    entityId: string,
): {
    definitions: AssignmentDefinitions;
    clinicianRecommendation: AssignmentClinicianRecommendation | null;
} => {
    const { assignmentDefinitions, assignmentResultsByVisitId } = useContext(BulkClinicianContext);
    return {
        definitions: assignmentDefinitions,
        clinicianRecommendation:
            assignmentResultsByVisitId.get(entityId)?.clinicianRecommendation ?? null,
    };
};

export const useClinicianRecommendations = (
    entityId: string,
    appointmentId: string,
): [clinicianRecommendations: ClinicianRecommendationFragment[], isLoading: boolean] => {
    const { state } = useContext(BulkClinicianContext);
    const recommendationsMap = useStoreValue(
        BulkClinicianContext,
        'recommendationsStore',
        entityId,
    );

    return [
        recommendationsMap ? recommendationsMap.get(appointmentId) ?? [] : [],
        state === 'loading',
    ];
};

export const useSelectedClinician = (
    entityId: string,
    appointmentId: string,
): [DecisionSupportSelection | null, (selection: DecisionSupportSelection | null) => void] => {
    const { selectedRecommendationStore, isEntitySelectedStore } = useContext(BulkClinicianContext);
    const selectedCliniciansMap = useStoreValue(
        BulkClinicianContext,
        'selectedRecommendationStore',
        entityId,
    );

    const selectedClinician = selectedCliniciansMap
        ? selectedCliniciansMap.get(appointmentId) ?? null
        : null;

    const setSelectedClinician = useCallback(
        (selection: DecisionSupportSelection | null) => {
            const current = selectedRecommendationStore.getSnapshot(entityId);
            const next = new Map(current ?? []);
            next.set(appointmentId, selection);
            selectedRecommendationStore.setValue(entityId, next);
            // Auto-select the entity when a clinician is chosen (mirrors useSelectedDate).
            if (selection) {
                isEntitySelectedStore.setValue?.(entityId, true);
            }
        },
        [selectedRecommendationStore, isEntitySelectedStore, entityId, appointmentId],
    );

    return [selectedClinician, setSelectedClinician];
};
